import { Overlay, OverlayConfig } from '@angular/cdk/overlay';
import { ComponentPortal, ComponentType } from '@angular/cdk/portal';
import { isPlatformBrowser } from '@angular/common';
import { inject, Injectable, Injector, PLATFORM_ID, signal, WritableSignal } from '@angular/core';
import { Subject } from 'rxjs';

import { HxDialogConfig } from './dialog-config';
import { HxDialogContainerComponent } from './dialog-container';
import { HxDialogRef } from './dialog-ref';
import { DIALOG_DATA, DIALOG_REF } from './internal/dialog-injectors';

/**
 * Service for opening modal dialogs
 *
 * Follows single-dialog policy: opening a new dialog automatically closes any existing dialog
 *
 * **IMPORTANT**: This service must be provided at the component level (not root) to ensure proper injector context.
 * This allows the service to access feature module providers like API tokens.
 *
 * @example
 *   ```typescript
 *   @Component({
 *     providers: [HxDialogService]  // ← Provide here
 *   })
 *   export class MyComponent {
 *     private dialogService = inject(HxDialogService);
 *
 *     openDialog() {
 *       this.dialogService.open(DialogComponent, { data: {...} });
 *     }
 *   }
 *   ```;
 */
@Injectable()
export class HxDialogService {
  private readonly overlay = inject(Overlay);
  private readonly injector = inject(Injector);
  private readonly platformId = inject(PLATFORM_ID);
  private readonly isBrowser = isPlatformBrowser(this.platformId);

  /** Signal tracking all currently open dialogs */
  readonly openDialogs: WritableSignal<readonly HxDialogRef[]> = signal([]);

  /** Subject that emits when any dialog is opened */
  private readonly _afterDialogOpened = new Subject<HxDialogRef>();

  /** Subject that emits when any dialog is closed */
  private readonly _afterAllClosed = new Subject<void>();

  /** Track if currently opening a dialog to prevent rapid-fire calls */
  private _isOpening = false;

  /**
   * Opens a dialog containing the given component
   *
   * @template T - Component type to load in dialog
   * @template D - Type of data passed to component
   * @template I - Type of inputs to bind to component
   * @template R - Type of result returned when dialog closes
   * @param component Component class to instantiate
   * @param config Configuration options
   * @returns Reference to opened dialog
   */
  open<T, D = any, I = any, R = any>(component: ComponentType<T>, config?: HxDialogConfig<D, I>): HxDialogRef<T, R> {
    // T137: Handle rapid open/close - debounce to prevent animation conflicts
    if (this._isOpening) {
      console.warn('[HxDialogService] Dialog is already opening. Ignoring duplicate call.');
      // Return existing dialog if available
      const existing = this.getOpenDialog();
      if (existing) {
        return existing as HxDialogRef<T, R>;
      }
    }

    this._isOpening = true;

    const fullConfig: HxDialogConfig<D, I> = {
      hasBackdrop: true,
      disableClose: false,
      closeOnBackdropClick: true,
      closeOnEscape: true,
      autoFocus: 'first-element',
      restoreFocus: true,
      size: 'medium',
      ...config,
    };

    if (!fullConfig.allowMultiple) {
      const existingDialog = this.getOpenDialog();
      if (existingDialog) {
        existingDialog.close();
        // Wait for close animation to complete
        setTimeout(() => this._createDialog(component, fullConfig), 300);
        return this._createDialogRef<T, R>();
      }
    }

    return this._createDialog(component, fullConfig);
  }

  private _createDialog<T, D, I, R>(component: ComponentType<T>, fullConfig: HxDialogConfig<D, I>): HxDialogRef<T, R> {
    const overlayRef = this.overlay.create(this._getOverlayConfig(fullConfig));

    // Create container component
    const containerPortal = new ComponentPortal(HxDialogContainerComponent, null, this.injector);
    const containerRef = overlayRef.attach(containerPortal);
    const container = containerRef.instance;
    container.config.set(fullConfig);

    // T031-T032: Create ComponentPortal for user component with custom injector
    const dialogRef = new HxDialogRef<T, R>(overlayRef, null as any, this._generateId());
    container.dialogRef = dialogRef;

    const userInjector = this._createInjector(fullConfig, dialogRef);
    const userPortal = new ComponentPortal(component, null, userInjector);
    const componentRef = container.attachComponentPortal(userPortal);

    // ✨ NEW: Set input properties directly on component instance
    if (fullConfig.inputs) {
      // Handle both signal-based inputs and traditional @Input() properties
      Object.entries(fullConfig.inputs).forEach(([key, value]) => {
        try {
          // Try using setInput for signal-based inputs
          componentRef.setInput(key, value);
        } catch {
          // Fallback to direct assignment for traditional @Input() properties
          (componentRef.instance as any)[key] = value;
        }
      });
      componentRef.changeDetectorRef.detectChanges();
    }

    // Update dialogRef with component instance
    (dialogRef as any).componentInstance = componentRef.instance;

    // T066: Prevent body scroll
    this._lockBodyScroll();

    // T035: Update openDialogs signal
    this.openDialogs.update((dialogs) => [...dialogs, dialogRef]);

    // T036: Emit afterOpened event after animation
    setTimeout(() => {
      dialogRef._notifyOpened();
      this._afterDialogOpened.next(dialogRef);
      this._isOpening = false; // Reset flag after dialog is fully opened
    }, 250);

    // T067-T138: Clean up when closed
    dialogRef.afterClosed().subscribe(() => {
      this._unlockBodyScroll();
      this.openDialogs.update((dialogs) => dialogs.filter((d) => d !== dialogRef));
      if (this.openDialogs().length === 0) {
        this._afterAllClosed.next();
      }
    });

    return dialogRef;
  }

  /** T137: Create placeholder dialog ref for queued dialogs */
  private _createDialogRef<T, R>(): HxDialogRef<T, R> {
    const placeholderRef = new HxDialogRef<T, R>(null as any, null as any, this._generateId());
    return placeholderRef;
  }

  private _lockBodyScroll(): void {
    if (this.isBrowser && this.openDialogs().length === 0) {
      document.body.classList.add('hx-dialog-open');
    }
  }

  private _unlockBodyScroll(): void {
    if (this.isBrowser && this.openDialogs().length === 0) {
      document.body.classList.remove('hx-dialog-open');
    }
  }

  private _getOverlayConfig(config: HxDialogConfig): OverlayConfig {
    let positionStrategy = this.overlay.position().global();

    // Use customPosition for fine-grained positioning if provided
    if (config.customPosition) {
      if (config.customPosition.top) {
        positionStrategy = positionStrategy.top(config.customPosition.top);
      }
      if (config.customPosition.right) {
        positionStrategy = positionStrategy.right(config.customPosition.right);
      }
      if (config.customPosition.bottom) {
        positionStrategy = positionStrategy.bottom(config.customPosition.bottom);
      }
      if (config.customPosition.left) {
        positionStrategy = positionStrategy.left(config.customPosition.left);
      }
    } else {
      // Apply position preset strategies
      const position = config.position || 'center';

      switch (position) {
        case 'left':
          positionStrategy = positionStrategy.left('1rem').centerVertically();
          break;
        case 'right':
          positionStrategy = positionStrategy.right('1rem').centerVertically();
          break;
        case 'top':
          positionStrategy = positionStrategy.top('1rem').centerHorizontally();
          break;
        case 'bottom':
          positionStrategy = positionStrategy.bottom('1rem').centerHorizontally();
          break;
        case 'center':
        default:
          positionStrategy = positionStrategy.centerHorizontally().centerVertically();
          break;
      }
    }

    return new OverlayConfig({
      hasBackdrop: config.hasBackdrop,
      backdropClass: config.backdropClass || 'hx-dialog-backdrop',
      panelClass: config.panelClass,
      positionStrategy,
      scrollStrategy: this.overlay.scrollStrategies.block(),
    });
  }

  private _createInjector<D, I>(config: HxDialogConfig<D, I>, dialogRef: HxDialogRef): Injector {
    return Injector.create({
      parent: this.injector,
      providers: [
        { provide: DIALOG_DATA, useValue: config.data },
        { provide: DIALOG_REF, useValue: dialogRef },
      ],
    });
  }

  private _generateId(): string {
    return `hx-dialog-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
  }

  /** Close all open dialogs */
  closeAll(): void {
    this.openDialogs().forEach((dialog) => dialog.close());
  }

  /** Gets the currently open dialog reference (if any) */
  getOpenDialog(): HxDialogRef | undefined {
    const dialogs = this.openDialogs();
    return dialogs.length > 0 ? dialogs[0] : undefined;
  }

  /** T136: Get dialog by ID */
  getDialogById(id: string): HxDialogRef | undefined {
    return this.openDialogs().find((dialog) => dialog.id === id);
  }
}
